/* eslint-disable camelcase */
// https://auth0.com/docs/libraries/auth0-single-page-app-sdk
// https://auth0.com/docs/libraries/auth0-single-page-app-sdk/migrate-from-auth0-js-to-the-auth0-single-page-app-sdk
// https://github.com/auth0/auth0-spa-js
import { Auth0Client } from "@auth0/auth0-spa-js";
import { EventEmitter } from "events";
import debugFactory from "debug";
import storage from "@teaming/common-storage";
import { AuthenticationEvents, AuthRedirectType, Source } from "@teaming/common-enums";
import { googleStandardOptions, msStandardOptions, emailStandardOptions } from "./oauthOptions";
import getAuthRedirectBehaviors from "./redirectBehaviors";

const debug = debugFactory( "teaming:common-io:authService" );

const EXPIRATION_POLL_AT_MS = 60000;
const EXPIRATION_REFRESH_AT_MIN = 5;

const eventDefaults = overrides => {
	return {
		source: "",
		loggedIn: false,
		user: null,
		error: null,
		state: {},
		token: "",
		redirectTo: "",
		completedSignup: false,
		...overrides,
	};
};

const signupOptionsMap = {
	DEFAULT: {
		authorizeOptions: {},
		uriSegment: "",
	},
	[ Source.GOOGLE ]: {
		authorizeOptions: googleStandardOptions,
		uriSegment: "google",
	},
	[ Source.OFFICE365 ]: {
		authorizeOptions: msStandardOptions,
		uriSegment: "microsoft",
	},
	[ Source.EMAIL ]: {
		authorizeOptions: emailStandardOptions,
		uriSegment: "email",
	},
};

const loginOptionsMap = {
	DEFAULT: {},
	[ Source.GOOGLE ]: googleStandardOptions,
	[ Source.OFFICE365 ]: msStandardOptions,
	[ Source.EMAIL ]: emailStandardOptions,
};

const redirectEventMaps = {
	[ AuthRedirectType.SIGNUP ]: {
		success: AuthRedirectType.SIGNUP,
		error: AuthenticationEvents.SIGNUP_ERROR,
	},
	[ AuthenticationEvents.LOGIN ]: {
		success: AuthenticationEvents.LOGIN,
		error: AuthenticationEvents.LOGIN_ERROR,
	},
};

export class AuthService extends EventEmitter {
	config = {};
	redirectBehaviors = {};
	webAuth = null;
	tokenExpiresAt = null;
	isCheckingSession = false;
	renewingPromise = null;
	expirationTicker = null;

	constructor( config ) {
		debug( "instantiating authService" );
		super();
		this.config = config;
		this.redirectBehaviors = getAuthRedirectBehaviors( { debug, config, } );
		this.webAuth = new Auth0Client( {
			domain: config.auth0Domain,
			client_id: config.auth0ClientId,
			redirect_uri: `${ config.appBaseUrl }/t2/callback?login=1`,
			// https://auth0.com/docs/libraries/auth0-single-page-app-sdk#use-rotating-refresh-tokens
			useRefreshTokens: false,
			// https://auth0.com/docs/libraries/auth0-single-page-app-sdk#change-storage-options
			cacheLocation: "localstorage",
		} );
	}

	async lookupUser() {
		debug( "looking up user" );
		return this.webAuth.getUser();
	}

	async _checkSession() {
		debug( "checking session" );
		this.isCheckingSession = true;
		try {
			const tokenResponse = await this.webAuth.getTokenSilently( { detailedResponse: true, } );
			const {
				access_token: accessToken,
				id_token: idToken,
				expires_in: expiresIn,
			} = tokenResponse;
			return { accessToken, idToken, expiresIn, };
		} finally {
			this.isCheckingSession = false;
		}
	}

	_storeAuthResult( authResult ) {
		debug( "storing auth result in memory", { authResult, } );
		// eslint-disable-next-line no-magic-numbers
		this.tokenExpiresAt = Date.now() + ( authResult.expiresIn * 1000 );
		storage.storeLoginFlag();

		if ( this.expirationTicker ) {
			clearInterval( this.expirationTicker );
			this.expirationTicker = null;
		}

		const debugExpiration = () => {
			// eslint-disable-next-line no-magic-numbers
			const expirationMinutes = ( ( this.tokenExpiresAt - Date.now() ) / 1000 ) / 60;

			debug( `token expires in ${ Math.floor( expirationMinutes ) } minutes` );

			if ( expirationMinutes <= EXPIRATION_REFRESH_AT_MIN ) {
				debug( "auto-renewing tokens" );
				this.renewTokens();
			}
		};

		// eslint-disable-next-line no-magic-numbers
		this.expirationTicker = setInterval( debugExpiration, EXPIRATION_POLL_AT_MS );

		debugExpiration();
	}

	_removeStoredAuthResult() {
		debug( "removing auth result from memory" );

		if ( this.expirationTicker ) {
			clearInterval( this.expirationTicker );
			this.expirationTicker = null;
		}

		storage.clearLoginFlag();
		this.tokenExpiresAt = null;
	}

	async isAuthenticated() {
		debug( "checking authentication" );
		if ( !( storage.isLoggedIn() && this.tokenExpiresAt ) ) {
			debug( "authentication has not occurred" );
			return false;
		}

		const hasExpirationElapsed = Math.floor( this.tokenExpiresAt - Date.now() ) <= 0;
		if ( hasExpirationElapsed ) {
			debug( "the token has expired" );
			return false;
		}

		return this.webAuth.isAuthenticated();
	}

	async getIdToken() {
		debug( "getting ID token" );
		return new Promise( async ( resolve, reject ) => {
			try {
				const claims = await this.webAuth.getIdTokenClaims();
				return resolve( claims?.__raw || "" );
			} catch ( e ) {
				reject( e );
			}
		} );
	}

	signup( source, appState ) {
		debug( "signing up", { source, appState, } );

		const { authorizeOptions, uriSegment, } = ( signupOptionsMap[ source ] || signupOptionsMap.DEFAULT );

		return this.webAuth.loginWithRedirect( {
			appState,
			...authorizeOptions,
			redirect_uri: `${ this.config.appBaseUrl }/t2/callback?signup=1&source=${ uriSegment }`,
			prompt: "select_account",
			screen_hint: "signup",
		} );
	}

	login( source, appState ) {
		debug( "logging in", { source, appState, } );

		return this.webAuth.loginWithRedirect( {
			appState,
			...( loginOptionsMap[ source ] || loginOptionsMap.DEFAULT ),
			prompt: "select_account",
			screen_hint: "login",
		} );
	}

	logout( { localOnly, } = { localOnly: false, } ) {
		debug( "logging out" );
		this.emit( AuthenticationEvents.LOGGING_OUT );

		setTimeout( () => {
			this._removeStoredAuthResult();

			this.emit( AuthenticationEvents.LOGOUT );

			this.webAuth.logout( {
				localOnly,
				returnTo: window.location.origin,
			} );
			// eslint-disable-next-line no-magic-numbers
		}, 500 );
	}

	renewTokens() {
		debug( "attempting to renew tokens" );
		if ( this.renewingPromise ) {
			return this.renewingPromise;
		}

		debug( "renewing tokens" );
		this.renewingPromise = new Promise( async ( resolve, reject ) => {
			if ( !storage.isLoggedIn() ) {
				return reject( "Not logged in" );
			}

			if ( this.isCheckingSession ) {
				return;
			}

			let sessionResult;
			try {
				sessionResult = await this._checkSession();
				this._storeAuthResult( sessionResult );
			} catch ( error ) {
				this._removeStoredAuthResult();
				return reject( error );
			}

			const { idToken, appState, } = sessionResult;

			try {
				const auth0User = await this.lookupUser();
				const payload = await this.redirectBehaviors[ AuthRedirectType.SILENT ]( sessionResult, auth0User );
				debug( "token renewal success" );
				this.emit(
					AuthenticationEvents.LOGIN,
					{
						...payload,
						appState,
						auth0User,
					}
				);
				resolve( { idToken, auth0User, } );
			} catch ( error ) {
				debug( "token renewal error" );
				this._removeStoredAuthResult();
				this.emit(
					AuthenticationEvents.LOGIN_ERROR,
					{
						error,
						appState,
					}
				);

				return reject( error );
			}
		} ).finally( () => {
			this.renewingPromise = null;
		} );

		return this.renewingPromise;
	}

	async handleAuthentication( { authRedirectType, } ) {
		debug( "handling authentication", { authRedirectType, } );

		let authResult;
		try {
			const { appState, } = await this.webAuth.handleRedirectCallback();
			const { idToken, accessToken, } = await this._checkSession();
			authResult = {
				appState,
				idToken,
				accessToken,
			};
			// FIXME: The only field the _storeAuthResult function is using is `expiresIn` and we're not passing it here? All other fields from authResult are being ignored.
			this._storeAuthResult( authResult );
		} catch ( error ) {
			this._removeStoredAuthResult();
			this.emit( AuthenticationEvents.LOGIN_ERROR, eventDefaults( {
				loggedIn: false,
				error,
			} ) );
			throw error;
		}

		const { appState, } = authResult;
		const { success: successEvent, error: errorEvent, } = redirectEventMaps[ authRedirectType ];

		try {
			const auth0User = await this.lookupUser();
			const payload = await this.redirectBehaviors[ authRedirectType ]( authResult, auth0User );
			this.emit( AuthenticationEvents.LOGGING_IN );
			this.emit(
				successEvent,
				{
					...payload,
					appState,
					auth0User,
				}
			);
		} catch ( error ) {
			this._removeStoredAuthResult();
			this.emit(
				errorEvent,
				{ error, appState, }
			);
			throw error;
		}
	}
}
